<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table">
				<thead class="sticky-header">
					<tr>
						<th class="icon-col"></th>
						<SortHeader
							title="name"
							:asc="currentSort.asc"
							:sort-key="currentSort.key"
							@click="() => setSort('name')"
							data-cy="report-table-header"
							class="header-cell"
							color="#695b59"
							weight="500"
						></SortHeader>
						<th>description</th>
						<th>student id</th>
						<th class="icon-col">
							<v-btn
								@click="studentsS.students.reload()"
								icon
								color="gray200"
								size="x-small"
								:loading="studentsS.students.loading"
								style="max-width: 20px; max-height: 20px"
							>
								<v-icon>mdi-reload</v-icon></v-btn
							>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="row in paginatedResults"
						:key="row.uid"
						:class="{
							'table-row': true,
							selected:
								(editModal.show && editModal.student === row) ||
								(editRelationModal.show && editRelationModal.student === row),
						}"
					>
						<td>
							<v-btn
								@click="updatePinUser(row)"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:loading="state.loadPinned.includes(row.uid)"
								:disabled="deleted"
							>
								<v-tooltip location="bottom" class="custom-tooltip" activator="parent">
									Lock Record
								</v-tooltip>
								<v-icon :color="row.pinned ? 'primary' : 'gray500'"
									>mdi-lock-outline</v-icon
								>
							</v-btn>
						</td>
						<td data-cy="table-col-name">
							{{ row.child_fname }} {{ row.child_lname }}
							<v-btn
								@click="
									editModal.show = true;
									editModal.student = row;
								"
								color="gray300"
								density="compact"
								icon
								variant="text"
							>
								<v-icon color="gray500">mdi-note-edit</v-icon>
							</v-btn>
						</td>
						<td data-cy="table-col-description">
							{{ row.description }}
						</td>
						<td data-cy="table-col-external-id">
							{{ row.external_child_id || row.external_id }}
						</td>
						<td>
							<v-btn
								@click="
									editRelationModal.show = true;
									editRelationModal.student = row;
								"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:disabled="deleted"
							>
								<v-icon color="gray500">mdi-account-group</v-icon>
							</v-btn>
						</td>
					</tr>
				</tbody>
			</table>

			<div
				v-if="studentsS.students.loading && !studentsS.students.data.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<LoadingState text="Loading Students"></LoadingState>
			</div>
			<div
				v-if="!studentsS.students.loading && !studentsS.students.data.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">We couldn't find any Students.</div>
					<p>Try reloading the page or adding a new student.</p>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination v-bind="forComponent()"></pagination>
		<div class="table-buttons">
			<v-btn @click="addModal.show = true">Create New</v-btn>
			<v-btn
				@click="uploadModal.show = true"
				color="#E5E4DB"
				append-icon="mdi-tray-arrow-up"
				>Upload</v-btn
			>

			<div class="d-flex align-center">
				<v-btn
					@click="download"
					:loading="state.loadDownload"
					color="#E5E4DB"
					append-icon="mdi-tray-arrow-down"
					>Download</v-btn
				>
				<div class="ml-1" tabindex="1">
					<v-tooltip location="bottom" activator="parent">Download Relations</v-tooltip>
					<RelationsDownload
						:download-types="['enrollment', 'team_enrollment', 'relationship']"
					></RelationsDownload>
				</div>
			</div>
		</div>
	</div>
	<edit-student-modal
		v-model:show="editModal.show"
		:student="editModal.student"
	></edit-student-modal>
	<add-student-modal
		v-model:show="addModal.show"
		@update:search="
			$emit('update:search', $event);
			addModal.show = false;
		"
	></add-student-modal>
	<student-relations-modal
		v-model:show="editRelationModal.show"
		:directory-entity="editRelationModal.student"
		:entity-type="'student'"
		:entity-id="editRelationModal.student.uid"
		:entity-name="`${editRelationModal.student.child_fname} ${editRelationModal.student.child_lname}`"
		:tabs="['parent', 'teacher', 'team', 'class']"
		@entity-click="handleEntityClick"
	></student-relations-modal>

	<upload-csv-modal v-model:show="uploadModal.show" :tab="props.tab" />
</template>

<script setup lang="ts">
import { reactive, watch } from "vue";
import usePaginate from "@/hooks/usePaginate";
import { computed } from "vue";
import pagination from "@/components/pagination.vue";
import downloadFile, { complexSearch } from "@/utils";
import type { StudentsType } from "@/api_types/school";
import EditStudentModal from "./editStudentModal.vue";
import AddStudentModal from "./addStudentModal.vue";
import useDirectoryNavigation from "@/stores/school/directoryNavigation";
import StudentRelationsModal from "@/components/school/directory/RelationshipModal.vue";
import useStudents from "@/stores/school/students";
import { useAlertsStore } from "@/stores/alerts";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import uploadCsvModal from "../uploadCsvModal.vue";
import FetchService from "@/services/fetch.service";
import RelationsDownload from "../relationsDownload.vue";
import SortHeader from "@/components/global/sortHeader.vue";
import useTableSort from "@/hooks/useTableSort";
import { sort } from "@/utils/sort";

const props = defineProps<{
	tab: "teacher" | "parent" | "student" | "team";
	search: string;
	deleted: boolean;
	selectedEntity?: any;
}>();
const emit = defineEmits<{
  (e: 'update:search', ...args: any[]): void;
}>();

const { currentSort, setSort } = useTableSort([
	{
		key: "name",
		title: "name",
		sortKey: "name",
	},
]);

const state = reactive({
	loadPinned: [] as string[],
	loadDownload: false,
});

const editModal = reactive({ show: false, student: {} as StudentsType[number] });
const addModal = reactive({ show: false });
const editRelationModal = reactive({ show: false, student: {} as StudentsType[number] });

const uploadModal = reactive({
	show: false,
});
const studentsS = useStudents();
const directoryNav = useDirectoryNavigation();
studentsS.students.reload();
const filteredData = computed(() => {
	return sort(
		studentsS.students.data?.filter((i) => {
			if (i.deleted !== props.deleted) return false;
			return complexSearch(
				props.search || "",
				`${i.child_fname} ${i.child_lname} ${i.external_id} ${i.external_child_id}`
			);
		}) || [],
		(i) => `${i.child_lname} ${i.child_fname}`,
		currentSort.value.asc
	);
});

const updatePinUser = async (student: StudentsType[number]) => {
	state.loadPinned.push(student.uid);
	const res = await studentsS.updateStudent(student.uid, {
		pinned: !student.pinned,
	});
	useAlertsStore().addAlert(
		res ? "success" : "error",
		res ? "Student saved successfully" : "Failed to update student",
		5
	);
	state.loadPinned = state.loadPinned.filter((str) => str !== student.uid);
};

const { forComponent, paginatedResults } = usePaginate(filteredData);

const download = async () => {
	state.loadDownload = true;
	const res = await FetchService.fetchData("/students/download", "get", undefined, true);
	const cType = res?.res?.headers.get?.("content-type") || "";
	if (typeof res?.data === "string" && cType.includes("text/csv")) {
		downloadFile(
			res.data,
			res?.res?.headers
				?.get("content-disposition")
				?.split("filename=")?.[1]
				?.replace(/"/g, "") || "students.csv",
			"text/csv"
		);
	} else {
		useAlertsStore().addAlert(
			"error",
			"Failed to download students, please try again later",
			10
		);
	}
	state.loadDownload = false;
};

// Handle entity clicks from relationship modal
const handleEntityClick = (entityData: any) => {
	// Navigate to the entity using the store
	directoryNav.navigateToEntity(entityData);
};

// Watch for selected entity changes and open the relationship modal
watch(() => directoryNav.selectedEntity, (newEntity) => {
	if (newEntity && newEntity.entityType === 'student') {
		// Find the student in the current data
		const student = studentsS.students.data.find(s => s.uid === newEntity.entityId);
		if (student) {
			editRelationModal.student = student;
			editRelationModal.show = true;
			// Clear the selected entity after opening the modal
			directoryNav.clearSelectedEntity();
		}
	}
}, { immediate: true });
</script>
<style scoped lang="scss">
.icon-col {
	width: 0px;
}
.table-row {
	td {
		transition: background-color 0.3s;
	}
	&.selected {
		td {
			background-color: #e6e6e689 !important;
		}
	}
}
</style>
